import React, { useState } from "react";
import { CreateShiftScreenContainer } from "../styledComponents";
import { AddShiftFormTypes, Position, Supervisor } from "../AddShiftForm.types";
import { PositionSelectField } from "../Components/PositionSelectField";
import { WageSelectField } from "../Components/WageSelectField";
import { SupervisorSelectField } from "../Components/SupervisorSelectField";
import { ShiftAdjustClockSection } from "../../TimesheetsPageTable/ShiftAdjust/ShiftAdjustClockSection";
import { ShiftAdjustMealBreaks } from "../../TimesheetsPageTable/ShiftAdjust/ShiftAdjustMealBreaks";
import { MealBreak } from "../../TimesheetsPageTable/ShiftAdjust/ShiftAdjustMealBreak";
import { getDurationInMinutes } from "../../breakUtils";

export type CreateShiftScreenProps = {
  formValues: AddShiftFormTypes;
  setFormValues: (v: AddShiftFormTypes) => void;
  setCurrentPosition: (v: Position) => void;
};

export const CreateShiftScreen = ({
  formValues,
  setFormValues,
  setCurrentPosition,
}: CreateShiftScreenProps) => {
  const [formattedTimezone, setFormattedTimezone] = useState("");

  const getAlternativeName = (timeZone: string) => {
    const opts: Intl.DateTimeFormatOptions = { timeZoneName: "longGeneric", timeZone };
    return Intl.DateTimeFormat("en-EN", opts).format(Date.now()).split(",")[1].trim();
  };

  const setPosition = (position: Position) => {
    const newFormattedTimezone = getAlternativeName(position.timezone);
    const newFormattedTimezoneString = `(${newFormattedTimezone})`;
    setFormattedTimezone(newFormattedTimezoneString);
    setCurrentPosition(position);
    setFormValues({
      ...formValues,
      position,
    });
  };

  const setSupervisor = (supervisor: Supervisor) => {
    setFormValues({
      ...formValues,
      supervisor,
    });
  };

  const setWage = (wage: number) => {
    setFormValues({
      ...formValues,
      wageOverride: wage,
    });
  };

  const setClockIn = (clockIn: Date) => {
    // Update existing meal breaks to use the new shift start date
    // This ensures meal break dates stay synchronized with shift start date
    const updatedBreaks = formValues.breaks?.map((mealBreak) => {
      const updatedMealBreak = { ...mealBreak };

      if (mealBreak.start) {
        const newStart = new Date(clockIn);
        newStart.setHours(
          mealBreak.start.getHours(),
          mealBreak.start.getMinutes(),
          mealBreak.start.getSeconds(),
          mealBreak.start.getMilliseconds(),
        );
        updatedMealBreak.start = newStart;
      }

      if (mealBreak.end) {
        const newEnd = new Date(clockIn);
        newEnd.setHours(
          mealBreak.end.getHours(),
          mealBreak.end.getMinutes(),
          mealBreak.end.getSeconds(),
          mealBreak.end.getMilliseconds(),
        );
        updatedMealBreak.end = newEnd;
      }

      return updatedMealBreak;
    });

    setFormValues({
      ...formValues,
      clockIn,
      breaks: updatedBreaks,
      breakDurationMins: getDurationInMinutes(updatedBreaks),
    });
  };

  const setClockOut = (clockOut: Date) => {
    setFormValues({
      ...formValues,
      clockOut,
    });
  };

  const handleUpdateMealBreak = (updatedMealBreak: MealBreak) => {
    const newBreaks = formValues.breaks?.map((mealBreak) =>
      mealBreak.id === updatedMealBreak.id ? updatedMealBreak : mealBreak,
    );
    setFormValues({
      ...formValues,
      breaks: newBreaks,
      breakDurationMins: getDurationInMinutes(newBreaks),
    });
  };

  const handleDeleteMealBreak = (id: string) => {
    const newBreaks = formValues.breaks?.filter((mealBreak) => mealBreak.id !== id);
    setFormValues({
      ...formValues,
      breaks: newBreaks,
      breakDurationMins: getDurationInMinutes(newBreaks),
    });
  };

  const handleAddMealBreak = () => {
    const newId = `meal-break-${Date.now()}`;
    // Default meal break start/end times to shift start time (clockIn)
    // This ensures the date portion matches the shift start date
    const defaultBreakTime = formValues.clockIn || new Date();
    const newBreak = {
      id: newId,
      start: defaultBreakTime,
      end: defaultBreakTime,
    };
    const newBreaks = formValues.breaks ? [...formValues.breaks, newBreak] : [newBreak];
    setFormValues({
      ...formValues,
      breaks: newBreaks,
      breakDurationMins: getDurationInMinutes(newBreaks),
    });
  };

  return (
    <CreateShiftScreenContainer>
      <PositionSelectField position={formValues.position} setPosition={setPosition} />
      <ShiftAdjustClockSection
        clockIn={formValues.clockIn}
        clockOut={formValues.clockOut}
        setClockIn={setClockIn}
        setClockOut={setClockOut}
        formattedTimezone={formattedTimezone}
      />
      <ShiftAdjustMealBreaks
        mealBreaks={formValues.breaks || []}
        onUpdateMealBreak={handleUpdateMealBreak}
        onDeleteMealBreak={handleDeleteMealBreak}
        onAddMealBreak={handleAddMealBreak}
        maxBreaks={3}
        formattedTimezone={formattedTimezone}
      />
      <SupervisorSelectField supervisor={formValues.supervisor} setSupervisor={setSupervisor} />
      <WageSelectField
        wage={formValues.wageOverride}
        placeholderWage={formValues.position?.baseWage}
        setWage={setWage}
      />
    </CreateShiftScreenContainer>
  );
};
